import React, { useState, useEffect } from 'react';
import { advanceAPI } from '../../../services';
import { 
  DollarSign, 
  Clock, 
  CheckCircle, 
  XCircle,
  Ban,
  Calendar,
  FileText,
  TrendingUp,
  RefreshCw,
  Filter
} from 'lucide-react';

const MyAdvanceRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchRequests();
  }, [filterStatus]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const status = filterStatus === 'all' ? null : filterStatus;
      const response = await advanceAPI.getMyAdvanceRequests(status);
      setRequests(response?.data || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setErrorMessage('Failed to load requests');
      setTimeout(() => setErrorMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this request?')) {
      return;
    }

    try {
      await advanceAPI.cancelAdvanceRequest(id);
      setSuccessMessage('Request cancelled successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchRequests();
    } catch (error) {
      console.error('Error cancelling request:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to cancel request');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pending' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Rejected' },
      disbursed: { bg: 'bg-blue-100', text: 'text-blue-800', icon: TrendingUp, label: 'Disbursed' },
      repaid: { bg: 'bg-gray-100', text: 'text-gray-800', icon: CheckCircle, label: 'Repaid' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-600', icon: Ban, label: 'Cancelled' }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-4 h-4" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="w-7 h-7 text-green-600" />
          My Advance/Loan Requests
        </h1>
        <p className="text-gray-600 mt-1">
          View and manage your advance and loan requests
        </p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {errorMessage && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800">{errorMessage}</p>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-500" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="all">All Requests</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="disbursed">Disbursed</option>
            <option value="rejected">Rejected</option>
            <option value="repaid">Repaid</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <button
          onClick={fetchRequests}
          className="flex items-center gap-2 px-4 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Requests List */}
      {requests.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-12 text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Requests Found</h3>
          <p className="text-gray-600">
            {filterStatus === 'all' 
              ? "You haven't submitted any advance or loan requests yet."
              : `No ${filterStatus} requests found.`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div
              key={request._id}
              className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${
                    request.type === 'advance' ? 'bg-green-100' : 'bg-blue-100'
                  }`}>
                    <DollarSign className={`w-6 h-6 ${
                      request.type === 'advance' ? 'text-green-600' : 'text-blue-600'
                    }`} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 capitalize">
                      {request.type}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Requested on {formatDate(request.requestedOn)}
                    </p>
                  </div>
                </div>
                {getStatusBadge(request.status)}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Amount</p>
                  <p className="text-xl font-bold text-gray-900">
                    {formatCurrency(request.amount)}
                  </p>
                </div>

                {request.type === 'loan' && request.emiMonths && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">EMI Details</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {formatCurrency(request.emiAmount)}/month
                    </p>
                    <p className="text-sm text-gray-600">
                      for {request.emiMonths} months
                    </p>
                  </div>
                )}

                {request.remainingAmount > 0 && request.status === 'disbursed' && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Remaining Balance</p>
                    <p className="text-lg font-semibold text-orange-600">
                      {formatCurrency(request.remainingAmount)}
                    </p>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-1">Reason</p>
                <p className="text-gray-900">{request.reason}</p>
              </div>

              {request.approverComments && (
                <div className="mb-4 bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600 mb-1">Comments</p>
                  <p className="text-gray-900">{request.approverComments}</p>
                </div>
              )}

              {request.rejectionReason && (
                <div className="mb-4 bg-red-50 rounded-lg p-3">
                  <p className="text-sm text-red-600 mb-1">Rejection Reason</p>
                  <p className="text-red-900">{request.rejectionReason}</p>
                </div>
              )}

              {/* Repayment History */}
              {request.repaymentHistory && request.repaymentHistory.length > 0 && (
                <div className="mt-4 border-t pt-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Repayment History</p>
                  <div className="space-y-2">
                    {request.repaymentHistory.map((payment, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          {payment.month}/{payment.year}
                        </span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(payment.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              {request.status === 'pending' && (
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => handleCancel(request._id)}
                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Ban className="w-4 h-4" />
                    Cancel Request
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyAdvanceRequests;
